import * as React from "react";
import { useContext, useState, useEffect } from "react";
import pluto from "@plutoxyz/frame-js";
const PlutoContext = React.createContext(
  void 0
);
const PlutoProvider = ({ children }) => {
  const [isReady, setIsReady] = React.useState(false);
  const [proof, setProof] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [logs, setLogs] = React.useState([]);
  const [isConnected, setIsConnected] = React.useState(false);
  const _pluto = pluto;
  React.useEffect(() => {
    (async () => {
      try {
        await pluto.initialize({
          onScriptLog: (message) => {
            setLogs((prevLogs) => [...prevLogs, message]);
          },
          onSuccess: (proof2) => {
            setProof(proof2);
          },
          onError: (error2) => {
            setError(error2);
          }
        });
        setIsReady(true);
      } catch (error2) {
        setError({
          message: "Failed to initialize Pluto",
          code: "PLUTO_INITIALIZATION_FAILED",
          display_message: "Failed to initialize Pluto"
        });
      }
    })();
  }, []);
  const connect = React.useCallback(
    async (code) => {
      if (!_pluto.isInitialized()) {
        throw new Error("Pluto is not ready yet. Wait for isReady to be true.");
      }
      if (_pluto.isConnected()) {
        throw new Error("Already connected to Pluto frame.");
      }
      try {
        await _pluto.connect(code);
        setIsConnected(true);
      } catch (err) {
        setError({
          message: err instanceof Error ? err.message : "Failed to connect to Pluto frame",
          code: "PLUTO_CONNECTION_FAILED",
          display_message: "Failed to connect to Pluto frame"
        });
        throw err;
      }
    },
    [isReady]
  );
  const resetConnection = React.useCallback(async () => {
    await _pluto.resetConnection();
    setIsConnected(false);
    setProof(null);
    setError(null);
    setLogs([]);
  }, []);
  const updateBrand = React.useCallback((brand) => {
    _pluto.updateBrand(brand);
  }, []);
  const updateEndpoints = React.useCallback((endpoints) => {
    _pluto.updateEndpoints(endpoints);
  }, []);
  return /* @__PURE__ */ React.createElement(
    PlutoContext.Provider,
    {
      value: {
        isReady,
        proof,
        error,
        logs,
        isConnected,
        connect,
        resetConnection,
        updateBrand,
        updateEndpoints
      }
    },
    children
  );
};
function usePluto() {
  const context = React.useContext(PlutoContext);
  if (context === void 0) {
    throw new Error("usePluto must be used within a PlutoProvider");
  }
  return context.isReady;
}
function useConnect() {
  const context = React.useContext(PlutoContext);
  if (context === void 0) {
    throw new Error("useConnect must be used within a PlutoProvider");
  }
  return {
    connect: context.connect,
    isConnected: context.isConnected
  };
}
function useProof() {
  const context = React.useContext(PlutoContext);
  if (context === void 0) {
    throw new Error("useProof must be used within a PlutoProvider");
  }
  return context.proof;
}
function useError() {
  const context = React.useContext(PlutoContext);
  if (context === void 0) {
    throw new Error("useError must be used within a PlutoProvider");
  }
  return context.error;
}
function useLogs() {
  const context = React.useContext(PlutoContext);
  if (context === void 0) {
    throw new Error("useLogs must be used within a PlutoProvider");
  }
  return context.logs;
}
function usePlutoFrame() {
  const context = React.useContext(PlutoContext);
  if (context === void 0) {
    throw new Error("usePlutoFrame must be used within a PlutoProvider");
  }
  const { connect, isConnected, isReady, resetConnection } = context;
  return {
    connect,
    isConnected,
    isReady,
    resetConnection
  };
}
const Frame = (props) => {
  const existingContext = useContext(PlutoContext);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  if (!isMounted) return null;
  const content = /* @__PURE__ */ React.createElement(_Frame, { ...props });
  return existingContext ? content : /* @__PURE__ */ React.createElement(PlutoProvider, null, content);
};
const _Frame = (props) => {
  const context = useContext(PlutoContext);
  const [draftScript, setDraftScript] = useState(props.script);
  const [frameKey, setFrameKey] = useState(0);
  useEffect(() => {
    if (!(context == null ? void 0 : context.isReady) || !props.script) return;
    (async () => {
      if (context.isConnected && draftScript !== props.script) {
        setFrameKey((k) => k + 1);
        await context.resetConnection();
        context.updateBrand(props.brand);
        context.connect(props.script);
      } else if (!(context == null ? void 0 : context.isConnected)) {
        context.updateBrand(props.brand);
        context.connect(props.script);
      }
      setDraftScript(props.script);
    })();
  }, [context == null ? void 0 : context.isReady, context == null ? void 0 : context.isConnected, props.script]);
  useEffect(() => {
    var _a;
    if (context == null ? void 0 : context.proof) {
      (_a = props.onProof) == null ? void 0 : _a.call(props, context.proof);
    }
  }, [context == null ? void 0 : context.proof]);
  useEffect(() => {
    var _a;
    if (context == null ? void 0 : context.error) {
      (_a = props.onError) == null ? void 0 : _a.call(props, context.error);
    }
  }, [context == null ? void 0 : context.error]);
  useEffect(() => {
    if (!(context == null ? void 0 : context.isConnected)) return;
    context == null ? void 0 : context.updateBrand(props.brand);
  }, [props.brand]);
  useEffect(() => {
    if ((context == null ? void 0 : context.isConnected) || !props.endpoints) return;
    context == null ? void 0 : context.updateEndpoints(props.endpoints);
  }, [props.endpoints]);
  return /* @__PURE__ */ React.createElement("div", { id: "pluto-frame", className: props.className, key: frameKey });
};
export {
  Frame,
  PlutoContext,
  PlutoProvider,
  useConnect,
  useError,
  useLogs,
  usePluto,
  usePlutoFrame,
  useProof
};
//# sourceMappingURL=index.es.js.map
